import React, { Fragment, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import TableMetaContext from 'src/utils/context/TableMetaContext';
import TableRowContext from 'src/utils/context/TableRowContext';
import { Link as PureLink } from 'react-router-dom';
import { isColumnHidden } from 'src/utils/form/dataGrid';
import { toSingular } from 'src/utils/core/word';
import {
  DELETE_ENTITY_MUTATION,
  DELETE_USER_ENTITY_MUTATION,
  DELETE_REPORT_ENTITY_MUTATION,
  DELETE_NOTIFICATION_ENTITY_MUTATION,
} from 'src/graphql/deleteEntity/mutation';
import { useMutation } from 'src/graphql';
import { notification } from 'src/components/Notification';
import { EntityDeleteConfirm } from 'src/components/EntityDeleteConfirm';
import { useActivityCreate } from 'src/hooks/activity';
import s from './TableBaseLink.styles';

const TableBaseLink = ({ children, withoutDelete, noLink, ...rest }) => {
  const { meta, forceRefetch, name } = useContext(TableMetaContext);
  const {
    id,
    index: tableRowIndex,
    onDelete: onTableRowDelete,
    deletedAt: entityDeletedAt,
    currentVersion,
  } = useContext(TableRowContext);
  const { deletedAt: versionDeletedAt } = currentVersion || {};
  const deletedAt = versionDeletedAt || entityDeletedAt;
  const [confirmActive, setConfirmActive] = useState(false);

  const [deleteMutation] = useMutation(DELETE_ENTITY_MUTATION);
  const [deleteUserEntityMutation] = useMutation(DELETE_USER_ENTITY_MUTATION);
  const [deleteReportEntityMutation] = useMutation(
    DELETE_REPORT_ENTITY_MUTATION,
  );
  const [deleteNotificationEntityMutation] = useMutation(
    DELETE_NOTIFICATION_ENTITY_MUTATION,
  );

  const createActivity = useActivityCreate();
  const onDelete = e => {
    e.preventDefault();
    e.stopPropagation();

    if (id) {
      return setConfirmActive(true);
    }
    if (onTableRowDelete) {
      onTableRowDelete(tableRowIndex);
    }
    return null;
  };

  const resourceName = toSingular(name);

  const onDeleteConfirm = async () => {
    let mutation = deleteMutation;
    if (['Role', 'User', 'FilteringRuleGroup'].includes(resourceName)) {
      mutation = deleteUserEntityMutation;
    }
    if (['ReportTemplate'].includes(resourceName)) {
      mutation = deleteReportEntityMutation;
    }
    if (['EmailTemplate', 'Notification'].includes(resourceName)) {
      mutation = deleteNotificationEntityMutation;
    }
    try {
      const response = await mutation({
        variables: {
          resourceName,
          id: +id,
        },
      });
      const responseData =
        response.data.deleteEntity ||
        response.data.deleteUserEntity ||
        response.data.deleteReportEntity ||
        response.data.deleteNotificationEntity;
      if (responseData?.successful) {
        if (
          [
            'Role',
            'User',
            'FilteringRuleGroup',
            'ReportTemplate',
            'EmailTemplate',
            'Notification',
          ].includes(resourceName)
        ) {
          createActivity({
            entityType: resourceName,
            entityId: +id,
            action: 'delete',
          });
        }
        forceRefetch();
      } else {
        notification.error('Unknown error deleting item');
      }
    } catch (e) {
      notification.error(e);
    }
  };

  const WrapperComponent = noLink ? 'div' : PureLink;
  return (
    <Fragment>
      <WrapperComponent {...rest}>
        {React.Children.map(children, (child, index) => {
          return React.cloneElement(child, {
            ...child.props,
            /* hidden: isColumnHidden(meta, index), */
          });
        })}
        {deletedAt && <s.DeletedIcon name="eye-cross" />}
        {!deletedAt && !withoutDelete && (
          <s.DeleteButtonWrapper className='deleteButton' onClick={onDelete}>
            <s.DeleteButton name="remove" />
          </s.DeleteButtonWrapper>
        )}
      </WrapperComponent>
      {confirmActive && (
        <EntityDeleteConfirm
          id={id}
          onClose={() => setConfirmActive(false)}
          onSubmit={onDeleteConfirm}
          resourceName={resourceName}
        />
      )}
    </Fragment>
  );
};

TableBaseLink.propTypes = {
  children: PropTypes.any,
  withoutDelete: PropTypes.bool,
  noLink: PropTypes.bool,
};

export default React.memo(TableBaseLink);
